'use client';
import React from 'react';
import { motion } from 'motion/react';
import { ShaderGradientCanvas, ShaderGradient } from '@shadergradient/react';
import LoginLeftMeshGradient from '@/lib/components/ui/special/LoginLeftMeshGradient';

const LoginLeft: React.FC = () => {
  return (
    <div className="relative h-full w-full overflow-hidden rounded-xl bg-slate-900">
      {/* Shader Gradient Background */}
      <div className="absolute inset-0 bg-slate-900 opacity-70">
        <LoginLeftMeshGradient />
        <ShaderGradientCanvas
          style={{
            width: '100%',
            height: '100%',
          }}
          lazyLoad={undefined}
          fov={undefined}
          pixelDensity={1}
          pointerEvents="none"
        >
          <ShaderGradient
            animate="on"
            type="sphere"
            wireframe={false}
            shader="defaults"
            uTime={0}
            uSpeed={0.2}
            uStrength={0.4}
            uDensity={0.9}
            uFrequency={4.5}
            uAmplitude={2.8}
            positionX={-0.2}
            positionY={0.1}
            positionZ={0}
            rotationX={0}
            rotationY={140}
            rotationZ={60}
            color1="#1f40af"
            color2="#7c3aed"
            color3="#0ea5e9"
            reflection={0.4}
            // View (camera) props
            cAzimuthAngle={120}
            cPolarAngle={120}
            cDistance={0.9}
            cameraZoom={2.5}
            // Effect props
            lightType="env"
            brightness={0.6}
            envPreset="dawn"
            grain="off"
            // Tool props
            toggleAxis={false}
            zoomOut={false}
            hoverState=""
            // Optional - if using transition features
            enableTransition={true}
          />
        </ShaderGradientCanvas>
      </div>

      {/* Energy Cards - Top Right */}
      <motion.div
        className="absolute top-6 right-6 z-10"
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        <div className="relative">
          {/* Solar Energy Card - Top */}
          <motion.div
            className="relative z-30 mb-[-8px] w-60 rounded-xl border border-white/25 bg-gradient-to-r from-yellow-500/15 to-orange-500/15 p-3 shadow-lg backdrop-blur-md"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-yellow-500/30">
                  <svg className="h-4 w-4 text-yellow-300" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,2L14.39,5.42C13.65,5.15 12.84,5 12,5C11.16,5 10.35,5.15 9.61,5.42L12,2M12,22L9.59,18.56C10.33,18.83 11.14,19 12,19C12.86,19 13.67,18.83 14.41,18.56L12,22Z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-medium text-yellow-200">Ηλιακή Ενέργεια</p>
                  <p className="text-sm font-bold text-white">45.2 kWh</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-yellow-300">+15.8%</p>
              </div>
            </div>
          </motion.div>

          {/* Wind Energy Card - Middle */}
          <motion.div
            className="relative z-20 ml-3 w-60 rounded-xl border border-white/25 bg-gradient-to-r from-cyan-500/15 to-blue-500/15 p-3 shadow-lg backdrop-blur-md"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-cyan-500/30">
                  <svg className="h-4 w-4 text-cyan-300" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-medium text-cyan-200">Αιολική Ενέργεια</p>
                  <p className="text-sm font-bold text-white">32.7 kWh</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-cyan-300">+8.4%</p>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Content */}
      <div className="relative z-10 flex h-full flex-col justify-between p-8 text-white">
        {/* Top section - A WISE QUOTE */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <motion.div
            className="mb-4 inline-block rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium tracking-wider backdrop-blur-sm"
            whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
          >
            ENEXAN ENERGY
          </motion.div>
        </motion.div>

        {/* Main content - Large text */}
        <motion.div
          className="max-w-md space-y-6"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <motion.h1
            className="text-5xl leading-tight font-bold tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            Ενισχύστε
            <br />
            το Μέλλον σας
            <br />
            Σήμερα
          </motion.h1>
          <motion.p
            className="text-lg leading-relaxed text-white/80"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            Μετασχηματίστε τη διαχείριση ενέργειας με έξυπνες λύσεις,
            <br />
            βιώσιμες πρακτικές και καινοτόμα τεχνολογία.
          </motion.p>
        </motion.div>

        {/* Bottom Energy Summary Card */}
        <motion.div
          className="max-w-xs"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0 }}
        >
          <motion.div
            className="rounded-xl border border-white/25 bg-gradient-to-r from-green-500/15 to-emerald-500/15 p-4 shadow-lg backdrop-blur-md"
            whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-r from-green-400 to-emerald-500">
                  <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17,8C8,10 5.9,16.17 3.82,21.34L5.71,22L6.66,19.7C7.14,19.87 7.64,20 8,20C19,20 22,3 22,3C21,5 14,5.25 9,6.25C4,7.25 2,11.5 2,13.5C2,15.5 3.75,17.25 3.75,17.25C7,8 17,8 17,8Z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-green-200">Συνολική Αποδοτικότητα</p>
                  <p className="text-xl font-bold text-white">94.8%</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-green-300">+7.2%</p>
                <p className="text-xs text-green-200">αυτό το μήνα</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginLeft;
